import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Database, Clock, Shield, ExternalLink, Users } from 'lucide-react';

const AboutOpenLEIsPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back Button */}
                <div className="mb-6">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Home
                    </Link>
                </div>

                {/* Page Header */}
                <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8 mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <Database className="h-8 w-8 text-primary-600" />
                        <h1 className="text-3xl font-bold text-gray-900">About OpenLEIs</h1>
                    </div>
                    <p className="text-lg text-gray-600">
                        Learn about our mission, data sources, and relationship with the global LEI
                        system
                    </p>
                </div>

                {/* Content */}
                <div className="space-y-8">
                    {/* What is Open LEIs for */}
                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                            What is the Open LEIs website for?
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                            It is a way to make the data contained in the LEI system more usable and
                            useful. OpenCorporates believes this will make reuse easier, help
                            identify quality problems and increase understanding of the LEI system.
                            As the LEI system develops over the coming months, we expect the Open
                            LEIs website to grow and evolve.
                        </p>
                    </div>

                    {/* Relationship to GLEIS */}
                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                            What is Open LEIs relationship to the GLEIS? Is it like the Central
                            Operating Unit?
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            The Central Operating Unit (see{' '}
                            <Link
                                to="/the-lei-system"
                                className="text-blue-600 hover:text-blue-800 underline"
                            >
                                "The LEI System"
                            </Link>
                            ) is the part of the LEI system that will coordinate and control the
                            activity of the federated local operating units and will have a formal
                            role in the management and operation of the GLEIS.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                            Open LEIs has no such formal role, but is a website and service to show
                            the potential for the LEI system, and help identify problems and issues.
                        </p>
                    </div>

                    {/* Data Source */}
                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <Database className="h-6 w-6 text-primary-600" />
                            <h2 className="text-2xl font-semibold text-gray-900">
                                Where did you get the data from?
                            </h2>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                            The Global Legal Entity Identifier Foundation (GLEIF), a supra-national
                            not-for-profit organization tasked to support the implementation and use
                            of the Legal Entity Identifier (LEI), provides authoritative
                            information, updated daily, on all LEIs issued to date, and produces
                            consolidated data dumps. Open LEIs regularly imports these and makes the
                            information available in a searchable, browsable form. We also make the
                            data for the individual entries available through a lightweight API (see
                            the API links on each page)
                        </p>
                    </div>

                    {/* Data Freshness */}
                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <Clock className="h-6 w-6 text-primary-600" />
                            <h2 className="text-2xl font-semibold text-gray-900">
                                How up-to-date are the data?
                            </h2>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                            We are importing the data every night, and show the timestamp of when it
                            was last changed on every page
                        </p>
                    </div>

                    {/* OpenCorporates & LEI */}
                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <Shield className="h-6 w-6 text-primary-600" />
                            <h2 className="text-2xl font-semibold text-gray-900">
                                What's OpenCorporates' relationship with the LEI system?
                            </h2>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                            OpenCorporates was on the initial advisory panel that let to the
                            founding of the LEI system, and has played an active part in the Private
                            Sector Preparatory Group that provides feedback to the GLEIS. We are
                            strong supporters of the system, and the public-access principles on
                            which it is based
                        </p>
                    </div>

                    {/* About OpenCorporates */}
                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <Users className="h-6 w-6 text-primary-600" />
                            <h2 className="text-2xl font-semibold text-gray-900">
                                What is OpenCorporates?
                            </h2>
                        </div>
                        <div className="prose max-w-none">
                            <p className="text-gray-700 leading-relaxed mb-4">
                                OpenCorporates is the largest openly licensed database of companies
                                in the world, with over 60 million companies in 80+ jurisdictions.
                                Our goal is to have an entry for every single legal entity in the
                                world – Open Street Map of the corporate world if you like. We also
                                make the data available under an open, Share-Alike Attribution
                                licence via our API. Those who wish to use the data without the
                                Share-Alike restrictions can do so under a non-share-alike licence,
                                and among are users are governments, major financial institutions
                                and business information companies.
                            </p>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                We believe that open corporate data is not just important for
                                scrutiny, understanding, and good business, it also has significant
                                quality benefits, and we recently started adding open corporate
                                network data to the information on the site.
                            </p>
                            <p className="text-gray-700 leading-relaxed">
                                We also developed the Open Company Data Index in partnership with
                                the World Bank Institute to highlight the issue of access to
                                statutory company data
                            </p>
                        </div>
                    </div>

                    {/* Privacy Policy */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                            Privacy Policy
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            Please read our User/Cookie Privacy Policy which sets out how we manage
                            your personal data in compliance with the General Data Protection
                            Regulation ("GDPR") which applies across the European Union (including
                            in the United Kingdom).
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                            Our separate Public Records Privacy Policy contains details of how we
                            manage personal data sourced from public records.
                        </p>
                    </div>

                    {/* More Information */}
                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                            Where can I find out more about OpenCorporates?
                        </h2>
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <ExternalLink className="h-4 w-4 text-gray-400" />
                                <span className="text-gray-700">Visit</span>
                                <a
                                    href="http://OpenCorporates.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                >
                                    OpenCorporates.com
                                </a>
                            </div>
                            <div className="flex items-center gap-2">
                                <ExternalLink className="h-4 w-4 text-gray-400" />
                                <span className="text-gray-700">Read our blog at</span>
                                <a
                                    href="http://blog.OpenCorporates.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                >
                                    blog.OpenCorporates.com
                                </a>
                            </div>
                            <div className="flex items-center gap-2">
                                <ExternalLink className="h-4 w-4 text-gray-400" />
                                <span className="text-gray-700">Follow us on Twitter at</span>
                                <a
                                    href="https://twitter.com/OpenCorporates"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                >
                                    @OpenCorporates
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Call to action */}
                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-8 text-center">
                        <h3 className="text-xl font-semibold text-primary-900 mb-4">
                            Ready to explore LEI data?
                        </h3>
                        <p className="text-primary-700 mb-6">
                            Start searching through our comprehensive database of Legal Entity
                            Identifiers.
                        </p>
                        <Link
                            to="/search"
                            className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                        >
                            <Database className="h-5 w-5" />
                            Search LEI Database
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AboutOpenLEIsPage;
